import { get_first_defined_value } from './service_layer_agreement.helpers';

const DURATION_UNIT_SECONDS = {
  day: 24 * 60 * 60,
  days: 24 * 60 * 60,
  d: 24 * 60 * 60,
  hour: 60 * 60,
  hours: 60 * 60,
  hr: 60 * 60,
  hrs: 60 * 60,
  h: 60 * 60,
  minute: 60,
  minutes: 60,
  min: 60,
  mins: 60,
  m: 60,
  second: 1,
  seconds: 1,
  sec: 1,
  secs: 1,
  s: 1,
};

function get_workflow_form_value(sla = {}) {
  const workflow_value = get_first_defined_value([
    sla.workflow_id,
    sla.entity_type_workflow,
    sla.entity_type_workflow_id,
    sla.workflow,
    '',
  ]);
  return workflow_value;
}

function build_duration_form_fields(sla = {}, format_duration) {
  const duration_fields = {
    target_duration: format_duration(sla.target_duration),
    warning_threshold: format_duration(sla.warning_threshold),
    escalation_duration: format_duration(sla.escalation_duration),
  };
  return duration_fields;
}

function build_project_form_fields(sla = {}) {
  const project_fields = {
    project_id: sla.project_id || sla.project || null,
  };
  return project_fields;
}

function get_validation_checks(create_form = {}, parse_duration_input) {
  const validation_checks = [
    {
      is_valid: Boolean(String(create_form.name || '').trim()),
      message: 'SLA name is required.',
    },
    {
      is_valid: Boolean(parse_duration_input(create_form.target_duration)),
      message: 'Target duration must be something like "2 days" or "48 hours".',
    },
    {
      is_valid: Boolean(parse_duration_input(create_form.warning_threshold)),
      message: 'Warning threshold must be something like "1 day 12 hours".',
    },
    {
      is_valid: Boolean(parse_duration_input(create_form.escalation_duration)),
      message: 'Escalation duration must be something like "3 days".',
    },
  ];
  return validation_checks;
}

function parse_duration_segments(raw_value = '') {
  const default_segments = ['0', raw_value];
  const segments = raw_value.includes(' ')
    ? raw_value.split(/\s+/, 2)
    : default_segments;
  return segments;
}

function parse_duration_numbers(raw_value = '') {
  const [day_part, time_part] = parse_duration_segments(raw_value);
  const [hours_raw, minutes_raw, seconds_raw] = String(time_part || '').split(':');
  return {
    days: Number(day_part),
    hours: Number(hours_raw),
    minutes: Number(minutes_raw),
    seconds: Number(seconds_raw),
  };
}

function has_invalid_duration_number(duration_numbers = {}) {
  return Object.values(duration_numbers).some((value) => Number.isNaN(value));
}

function append_duration_part(parts, value, unit) {
  if (value > 0) {
    const suffix = value === 1 ? unit : `${unit}s`;
    parts.push(`${value} ${suffix}`);
  }
}

function get_duration_tokens(raw_value = '') {
  const normalized_value = raw_value
    .toLowerCase()
    .replace(/,/g, ' ')
    .replace(/\band\b/g, ' ')
    .replace(/(\d)([a-z])/gi, '$1 $2')
    .replace(/([a-z])(\d)/gi, '$1 $2');
  return normalized_value.split(/\s+/).filter(Boolean);
}

function parse_duration_token_pair(tokens = [], index = 0) {
  const amount = Number(Reflect.get(tokens, index));
  const seconds_per_unit = Reflect.get(DURATION_UNIT_SECONDS, tokens[index + 1]) || null;
  return { amount, seconds_per_unit };
}

function is_valid_token_pair(amount, seconds_per_unit) {
  return Number.isFinite(amount) && seconds_per_unit;
}

function accumulate_pair(total_seconds, amount, seconds_per_unit) {
  return total_seconds + Math.round(amount * seconds_per_unit);
}

function process_duration_tokens(tokens) {
  let total_seconds = 0;
  for (let index = 0; index < tokens.length - 1; index += 2) {
    total_seconds += calculate_token_pair(tokens, index);
  }
  return total_seconds;
}

function calculate_token_pair(tokens, index) {
  const { amount, seconds_per_unit } = parse_duration_token_pair(tokens, index);
  if (!is_valid_token_pair(amount, seconds_per_unit)) {
    return 0;
  }
  return accumulate_pair(0, amount, seconds_per_unit);
}

export function accumulate_duration_tokens(tokens = []) {
  if (tokens.length < 2) {
    return null;
  }
  const total_seconds = process_duration_tokens(tokens);
  return total_seconds > 0 ? total_seconds : null;
}

function serialize_duration_seconds(total_seconds) {
  const hours = Math.floor(total_seconds / 3600);
  const minutes = Math.floor((total_seconds % 3600) / 60);
  const seconds = total_seconds % 60;
  const clock_values = [hours, minutes, seconds].map((value) => String(value).padStart(2, '0'));
  return clock_values.join(':');
}

function build_duration_parts(duration_numbers) {
  const total_hours = (duration_numbers.days * 24) + duration_numbers.hours;
  const parts = [];

  append_duration_part(parts, Math.floor(total_hours / 24), 'day');
  append_duration_part(parts, total_hours % 24, 'hour');
  append_duration_part(parts, duration_numbers.minutes, 'minute');
  append_duration_part(
    parts,
    parts.length === 0 ? duration_numbers.seconds : 0,
    'second'
  );

  return parts;
}

function is_blank_or_dash(raw_value) {
  return !raw_value || raw_value === '-';
}

function format_valid_duration(raw_value) {
  const duration_numbers = parse_duration_numbers(raw_value);
  if (has_invalid_duration_number(duration_numbers)) {
    return raw_value;
  }
  const parts = build_duration_parts(duration_numbers);
  return parts.length ? parts.join(' ') : '0 minutes';
}

export function format_duration_value(duration_value) {
  const raw_value = String(duration_value ?? '').trim();
  if (is_blank_or_dash(raw_value)) {
    return '-';
  }

  return format_valid_duration(raw_value);
}

function parse_duration_seconds(raw_value) {
  const token_seconds = accumulate_duration_tokens(get_duration_tokens(raw_value));
  if (token_seconds != null) {
    return token_seconds;
  }

  const numeric_value = Number(raw_value);
  return Number.isFinite(numeric_value) && numeric_value >= 0
    ? Math.round(numeric_value * 3600)
    : null;
}

function normalize_duration(raw_value) {
  const total_seconds = parse_duration_seconds(raw_value);
  return total_seconds == null
    ? null
    : serialize_duration_seconds(total_seconds);
}

export function parse_duration_input_value(duration_value) {
  const raw_value = String(duration_value ?? '').trim();
  if (!raw_value) {
    return null;
  }

  if (/^\d+:\d{2}:\d{2}$/.test(raw_value)) {
    return raw_value;
  }

  return normalize_duration(raw_value);
}

export function build_update_form_data(sla = {}, format_duration) {
  const workflow_value = get_workflow_form_value(sla);
  const duration_fields = build_duration_form_fields(sla, format_duration);
  const project_fields = build_project_form_fields(sla);
  const update_form = {
    id: sla.id || null,
    name: sla.name || '',
    description: sla.description || '',
    ...duration_fields,
    is_active: sla.is_active !== false,
    ...project_fields,
    workflow_id: workflow_value,
    workflow_locked: Boolean(workflow_value),
  };
  return update_form;
}

export function get_create_form_validation_message(create_form = {}, parse_duration_input) {
  const validation_checks = get_validation_checks(create_form, parse_duration_input);
  const failed_validation = validation_checks.find((check) => !check.is_valid) || null;
  const validation_message = failed_validation?.message || '';
  return validation_message;
}

function build_base_create_payload(create_form = {}, parse_duration_input) {
  const payload = {
    name: String(create_form.name || '').trim(),
    description: String(create_form.description || '').trim(),
    target_duration: parse_duration_input(create_form.target_duration),
    warning_threshold: parse_duration_input(create_form.warning_threshold),
    escalation_duration: parse_duration_input(create_form.escalation_duration),
    is_active: Boolean(create_form.is_active),
  };
  return payload;
}

function get_create_form_project_id(create_form = {}) {
  const project_value = get_first_defined_value([
    create_form.project_id,
    create_form.project,
    null,
  ]);
  const project_id = Number(project_value);
  const normalized_project_id = project_id > 0 ? project_id : null;
  return normalized_project_id;
}

function attach_project_id(payload = {}, project_id = null) {
  if (project_id) {
    Reflect.set(payload, 'project_id', project_id);
  }
}

export function build_create_payload_from_form(create_form = {}, parse_duration_input) {
  const payload = build_base_create_payload(create_form, parse_duration_input);
  const project_id = get_create_form_project_id(create_form);
  attach_project_id(payload, project_id);
  return payload;
}
